import { isArrayIndex, parsePath } from "./path.js";
import { parseRefPointer } from "./refs.js";
/**
 * Create a schema context from a root schema.
 */
export function createSchemaContext(rootSchema) {
    return { rootSchema };
}
/**
 * Resolve a $ref JSON pointer to its target schema.
 * Supports full JSON pointer syntax (RFC 6901), e.g.:
 * - #/definitions/Person
 * - #/$defs/Address
 * - #/properties/nested/items
 */
export function resolveRef(ctx, schema) {
    if (!schema.$ref)
        return schema;
    const segments = parseRefPointer(schema.$ref);
    if (segments.length === 0)
        return schema;
    let current = ctx.rootSchema;
    for (const segment of segments) {
        if (current == null || typeof current !== 'object')
            return schema;
        current = current[segment];
    }
    if (!current || typeof current !== 'object')
        return schema;
    return current;
}
/**
 * Resolve schema at a given path, handling $ref at each level.
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Schema resolution requires handling multiple nested cases
export function resolveSchemaAtPath(ctx, path) {
    if (!path)
        return ctx.rootSchema;
    const segments = parsePath(path);
    let current = ctx.rootSchema;
    for (const segment of segments) {
        if (!current)
            return undefined;
        current = resolveRef(ctx, current);
        if (isArrayIndex(segment)) {
            if (current.type === 'array' && current.items) {
                const items = current.items;
                current = Array.isArray(items)
                    ? items[Number.parseInt(segment, 10)]
                    : items;
            }
            else {
                return undefined;
            }
            continue;
        }
        if (current.type === 'object' || current.properties || current.additionalProperties || current.patternProperties) {
            const props = current.properties;
            if (props?.[segment]) {
                current = props[segment];
                continue;
            }
            // Check patternProperties for map types (e.g., Record<string, T>)
            if (current.patternProperties) {
                const patternProps = current.patternProperties;
                let matched = false;
                for (const [pattern, patternSchema] of Object.entries(patternProps)) {
                    if (new RegExp(pattern).test(segment)) {
                        current = patternSchema;
                        matched = true;
                        break;
                    }
                }
                if (matched)
                    continue;
            }
            if (current.additionalProperties && typeof current.additionalProperties === 'object') {
                current = current.additionalProperties;
                continue;
            }
            return undefined;
        }
        if (current.oneOf || current.anyOf) {
            const variants = (current.oneOf ?? current.anyOf);
            let found = false;
            for (const variant of variants) {
                const resolvedVariant = resolveRef(ctx, variant);
                // Check if this variant has the property we're looking for
                const props = resolvedVariant.properties;
                if (props?.[segment]) {
                    current = props[segment];
                    found = true;
                    break;
                }
            }
            if (!found)
                return undefined;
            continue;
        }
        return undefined;
    }
    return current ? resolveRef(ctx, current) : undefined;
}
/**
 * Get the type of a schema, resolving $ref if needed.
 */
export function getSchemaType(ctx, schema) {
    const resolved = resolveRef(ctx, schema);
    if (resolved.type)
        return resolved.type;
    if (resolved.oneOf || resolved.anyOf)
        return 'union';
    if (resolved.properties)
        return 'object';
    if (resolved.items)
        return 'array';
    return undefined;
}
/**
 * Check if a field is required in its parent schema.
 */
export function isFieldRequired(ctx, parentSchema, fieldName) {
    const resolved = resolveRef(ctx, parentSchema);
    return resolved.required?.includes(fieldName) ?? false;
}
/**
 * Get property names from a schema, resolving $ref if needed.
 */
export function getPropertyNames(ctx, schema) {
    const resolved = resolveRef(ctx, schema);
    return resolved.properties ? Object.keys(resolved.properties) : [];
}
/**
 * Find the discriminator property in a oneOf/anyOf schema.
 * Looks for a property with const values that uniquely identify each variant.
 */
export function getDiscriminatorProperty(ctx, schema) {
    const resolved = resolveRef(ctx, schema);
    const variants = (resolved.oneOf ?? resolved.anyOf);
    if (!variants?.length)
        return undefined;
    // Resolve first variant
    const firstVariant = resolveRef(ctx, variants[0]);
    if (!firstVariant.properties)
        return undefined;
    for (const propName of Object.keys(firstVariant.properties)) {
        const prop = firstVariant.properties[propName];
        // Check for const value OR single-value enum (both indicate a discriminator)
        const firstConstValue = prop.const ?? (Array.isArray(prop.enum) && prop.enum.length === 1 ? prop.enum[0] : undefined);
        if (firstConstValue !== undefined) {
            const values = new Set();
            let allHaveConst = true;
            for (const variant of variants) {
                const resolvedVariant = resolveRef(ctx, variant);
                const variantProps = resolvedVariant.properties;
                if (!variantProps?.[propName]) {
                    allHaveConst = false;
                    break;
                }
                const variantProp = variantProps[propName];
                // Check for const OR single-value enum
                const constValue = variantProp.const ??
                    (Array.isArray(variantProp.enum) && variantProp.enum.length === 1 ? variantProp.enum[0] : undefined);
                if (constValue === undefined) {
                    allHaveConst = false;
                    break;
                }
                values.add(constValue);
            }
            if (allHaveConst && values.size === variants.length)
                return propName;
        }
    }
    return undefined;
}
/**
 * Get discriminator options from a oneOf/anyOf schema.
 */
export function getDiscriminatorOptions(ctx, schema, discriminatorProperty) {
    const resolved = resolveRef(ctx, schema);
    const variants = (resolved.oneOf ?? resolved.anyOf);
    if (!variants?.length)
        return [];
    return variants
        .map((variant) => {
        const variantSchema = variant;
        const resolvedVariant = resolveRef(ctx, variantSchema);
        const props = resolvedVariant.properties;
        const prop = props?.[discriminatorProperty];
        // Check for const OR single-value enum
        const value = (prop?.const ??
            (Array.isArray(prop?.enum) && prop?.enum.length === 1 ? prop?.enum[0] : undefined));
        if (value === undefined)
            return null;
        const label = resolvedVariant.title ?? prop?.title ?? value;
        // Include the original $ref for shape-based config lookup
        return { value, label, schema: resolvedVariant, ref: variantSchema.$ref };
    })
        .filter((opt) => opt !== null);
}
/**
 * Find a variant schema by discriminator value.
 */
export function findVariantSchema(ctx, schema, discriminatorProperty, discriminatorValue) {
    return getDiscriminatorOptions(ctx, schema, discriminatorProperty).find((opt) => opt.value === discriminatorValue)
        ?.schema;
}
/**
 * Get visible property names from a schema, filtered and sorted.
 * - Filters out properties marked as hidden in formConfig
 * - Filters out properties in the exclude list
 * - Sorts by formConfig.order (ascending, undefined = MAX_SAFE_INTEGER)
 */
export function getVisibleProperties(ctx, schema, options = {}) {
    const { exclude, formConfig, basePath = '' } = options;
    let props = getPropertyNames(ctx, schema);
    // Filter by exclude list
    if (exclude?.length) {
        props = props.filter((p) => !exclude.includes(p));
    }
    // Filter by hidden config
    if (formConfig) {
        props = props.filter((p) => {
            const fieldPath = basePath ? `${basePath}.${p}` : p;
            return !formConfig[fieldPath]?.hidden;
        });
    }
    // Sort by config.order
    if (formConfig) {
        return [...props].sort((a, b) => {
            const aPath = basePath ? `${basePath}.${a}` : a;
            const bPath = basePath ? `${basePath}.${b}` : b;
            const aOrder = formConfig[aPath]?.order ?? Number.MAX_SAFE_INTEGER;
            const bOrder = formConfig[bPath]?.order ?? Number.MAX_SAFE_INTEGER;
            return aOrder - bOrder;
        });
    }
    return props;
}
